/**
 * Decides when "Nyt i Mesterplan" opens by itself.
 *
 * The rule: once per release, at launch, never in the middle of something. It waits until it knows
 * who the user is and what they have read, opens on the unread set it finds, and does not come
 * back until the page is loaded again. Port of Trackrs' `hooks/useWhatsNewPrompt.ts`.
 *
 * Yield order (plan §5.4): the Trackrs-attention intro goes first. A held-back prompt does not
 * claim the session, so it opens as soon as the intro is closed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AnnouncementDto } from '@mesterplan/api-client';
import { useAnnouncementFeed, useMarkAnnouncementsRead, useProductNewsPreference } from '@/hooks/useAnnouncements';
import { useNetUser } from '@/hooks/useNetUser';
import { useTrackrsAttentionIntroPending } from '@/components/integration/attention/TrackrsAttentionIntroModal';

/** The launch dialog shows this many notes; the rest are on the page ("Se alle nyheder"). */
export const WHATS_NEW_LAUNCH_MAX = 5;

export interface WhatsNewPromptState {
  isSignedIn: boolean;
  isFeedLoading: boolean;
  hasFeedError: boolean;
  unreadCount: number;
  /** `undefined` = not arrived yet, which must read as "wait", never as "no". */
  showAutomatically: boolean | undefined;
  /** Another launch prompt (the Trackrs-attention intro) is open or about to open. */
  isSuppressed: boolean;
  shownThisSession: boolean;
}

export function shouldOpenWhatsNew(state: WhatsNewPromptState): boolean {
  return (
    state.isSignedIn &&
    // Exactly true: a preference that is still loading is not permission to open.
    state.showAutomatically === true &&
    !state.isFeedLoading &&
    !state.hasFeedError &&
    state.unreadCount > 0 &&
    !state.isSuppressed &&
    !state.shownThisSession
  );
}

/**
 * Which user the prompt has already been shown to in this page session. Module-level on purpose: a
 * session ends with a full reload, which is exactly when this resets. Keyed by user so signing in
 * as somebody else without reloading still shows them their own news.
 */
let shownForUserId: string | null = null;

/** Clears the once-per-session flag. For tests, which share one module instance. */
export function resetWhatsNewPromptSession(): void {
  shownForUserId = null;
}

export interface WhatsNewPrompt {
  visible: boolean;
  /** The (at most WHATS_NEW_LAUNCH_MAX) unread notes as they were when the prompt opened; a later refresh must not reshuffle it. */
  items: AnnouncementDto[];
  /** How many notes were unread when the prompt opened (can exceed `items.length`); for the count label. */
  totalUnread: number;
  /** Luk: marks the notes the prompt showed as read (and only those). */
  close: () => Promise<void>;
  /** "Vis ikke nyheder automatisk": switch the prompt off for good, then close this one. */
  optOut: () => Promise<void>;
}

interface ShownState {
  userId: string;
  items: AnnouncementDto[];
  totalUnread: number;
}

export function useWhatsNewPrompt(): WhatsNewPrompt {
  const { user } = useNetUser();
  const feed = useAnnouncementFeed();
  const { markRead } = useMarkAnnouncementsRead();
  const { setShowAutomatically } = useProductNewsPreference();
  const trackrsIntroPending = useTrackrsAttentionIntroPending();

  const [shown, setShown] = useState<ShownState | null>(null);
  const shownRef = useRef<ShownState | null>(null);
  shownRef.current = shown;

  const userId = user?.id ?? null;
  const isSuppressed = trackrsIntroPending;

  useEffect(() => {
    // This mount has already had its turn for this user, including after close.
    if (shownRef.current?.userId === userId) return;
    if (!userId) return;
    const open = shouldOpenWhatsNew({
      isSignedIn: true,
      isFeedLoading: feed.isLoading,
      hasFeedError: feed.isError,
      unreadCount: feed.unreadCount,
      showAutomatically: feed.showAutomatically,
      isSuppressed,
      shownThisSession: shownForUserId === userId,
    });
    if (!open) {
      // The first successful feed load is this session's one chance. If it did not open (nothing
      // unread, opted out), claim the session anyway, so a later focus refetch that brings a newly
      // seeded note cannot pop the prompt open mid-session. A held-back prompt (suppressed) or a
      // feed that has not arrived does not claim.
      if (!isSuppressed && !feed.isLoading && !feed.isError && feed.showAutomatically !== undefined) {
        shownForUserId = userId;
      }
      return;
    }
    // Claim the session before rendering, so a re-render mid-open cannot open twice.
    shownForUserId = userId;
    // Store only what the surface shows: closing marks exactly these read, never notes 6+ unseen.
    setShown({
      userId,
      items: feed.unreadItems.slice(0, WHATS_NEW_LAUNCH_MAX),
      totalUnread: Math.max(feed.unreadCount, feed.unreadItems.length),
    });
  }, [userId, feed.isLoading, feed.isError, feed.unreadCount, feed.showAutomatically, feed.unreadItems, isSuppressed]);

  const close = useCallback(async () => {
    const current = shownRef.current;
    const covered = current?.items ?? [];
    setShown(current ? { userId: current.userId, items: [], totalUnread: 0 } : null);
    if (covered.length === 0) return;
    try {
      await markRead(covered.map((item) => item.id));
    } catch (error) {
      // Never trap the reader behind a failed write. The notes stay unread, so the badge keeps
      // counting them and the next launch offers them again.
      console.error('Failed to mark the notes shown at launch as read:', error);
    }
  }, [markRead]);

  const optOut = useCallback(async () => {
    // Start the write, close without waiting for it: after a click that means "go away", the dialog
    // must not sit there while the request finishes. A failed write rolls back and the switch on
    // the page is still there to try again.
    const written = setShowAutomatically(false).catch((error: unknown) => {
      console.error('Failed to switch Nyt i Mesterplan off:', error);
    });
    await close();
    await written;
  }, [setShowAutomatically, close]);

  const items = shown?.items ?? [];
  return { visible: items.length > 0, items, totalUnread: shown?.totalUnread ?? 0, close, optOut };
}
